import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';
import { staffApi, ServicesResponse, inr } from './staffApi';

const TYPE_LABEL: Record<string, string> = { pt: 'Personal training', spa: 'Spa & recovery', consult: 'Consults', class: 'Classes' };

/**
 * What a provider delivers (the bookables they can be booked for — the same
 * list the Schedule page's staff picker edits) and their own price on
 * offerings where members choose a coach.
 */
export default function StaffServicesTab({ mappingId, name }: { mappingId: string; name: string }) {
  const [data, setData] = useState<ServicesResponse | null>(null);
  const [delivers, setDelivers] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = () =>
    staffApi<ServicesResponse>(`/${mappingId}/services`)
      .then(r => {
        setData(r);
        setDelivers(new Set(r.bookables.filter(b => b.delivers).map(b => b.id)));
        setOverrides(Object.fromEntries(r.offerings.map(o => [`${o.ownerType}:${o.id}`, o.override != null ? String(o.override) : ''])));
      })
      .catch(e => toast.error(e.message));
  useEffect(() => { load(); }, [mappingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const out = new Map<string, ServicesResponse['bookables']>();
    for (const b of data?.bookables ?? []) {
      if (!out.has(b.bookableType)) out.set(b.bookableType, []);
      out.get(b.bookableType)!.push(b);
    }
    return [...out.entries()];
  }, [data]);

  if (!data) return <div className="p-8 space-y-3">{[0, 1, 2].map(i => <div key={i} className="h-12 rounded-xl bg-zinc-100 animate-pulse" />)}</div>;

  const toggle = (id: string) => setDelivers(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setSaving(true);
    try {
      await staffApi(`/${mappingId}/services`, {
        method: 'PUT',
        body: {
          classIds: [...delivers],
          prices: data.offerings.map(o => ({ ownerType: o.ownerType, ownerId: o.id, price: overrides[`${o.ownerType}:${o.id}`] === '' ? null : Number(overrides[`${o.ownerType}:${o.id}`]) })),
        },
      });
      toast.success('Saved');
      await load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-black text-zinc-900">Delivers</h3>
          <p className="text-xs text-zinc-500">What members can book {name.split(' ')[0]} for. Leaving a bookable with no one ticked keeps it open to everyone of the right role.</p>
        </div>
        {groups.length === 0 && <p className="text-sm text-zinc-400">No staffed bookables yet — add them on the Schedule page.</p>}
        {groups.map(([type, list]) => (
          <div key={type} className="space-y-2">
            <div className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.16em]">{TYPE_LABEL[type] ?? type}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {list.map(b => {
                const on = delivers.has(b.id);
                return (
                  <button
                    key={b.id}
                    onClick={() => toggle(b.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${on ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 hover:border-zinc-400'}`}
                  >
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${on ? 'bg-lime-400 text-zinc-900' : 'border border-zinc-300'}`}>{on && <Check className="w-3.5 h-3.5" />}</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold truncate">{b.name}</span>
                      <span className={`block text-[11px] ${on ? 'text-white/60' : 'text-zinc-400'}`}>
                        {b.scheduleMode === 'appointments' ? 'Appointments' : b.scheduleMode === 'timetable' ? 'Timetable' : 'Grid'}
                        {b.openToRole && !on ? ' · open to all' : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-black text-zinc-900">Price with {name.split(' ')[0]}</h3>
          <p className="text-xs text-zinc-500">
            Offerings where members choose their coach. Leave blank to use the {data.level ? `${data.level.name} level` : 'base'} price; set one to override it (a star coach).
          </p>
        </div>
        {data.offerings.length === 0 ? (
          <p className="text-sm text-zinc-400">None yet. On a plan or service, set “Who delivers it” to “Member picks a coach”.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-zinc-200">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] font-black text-zinc-400 uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2">Offering</th>
                  <th className="text-right px-3 py-2">Base</th>
                  <th className="text-right px-3 py-2">{data.level?.name ?? 'Level'}</th>
                  <th className="text-right px-3 py-2 w-40">Own price</th>
                  <th className="text-right px-3 py-2">Members pay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.offerings.map(o => {
                  const key = `${o.ownerType}:${o.id}`;
                  const own = overrides[key];
                  const pays = own !== '' && own != null ? Number(own) : o.levelPrice ?? o.basePrice;
                  return (
                    <tr key={key}>
                      <td className="px-3 py-2">
                        <div className="font-bold text-zinc-900 truncate">{o.name}</div>
                        <div className="text-[11px] text-zinc-400">{o.ownerType === 'plan' ? 'Plan' : 'Service'}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{inr(o.basePrice)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{o.levelPrice != null ? inr(o.levelPrice) : '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number" min={0} value={own ?? ''} placeholder="—"
                          onChange={e => setOverrides(m => ({ ...m, [key]: e.target.value }))}
                          className="w-28 px-2 py-1.5 border border-zinc-200 rounded-lg text-right tabular-nums outline-none focus:border-zinc-900"
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-black text-zinc-900">{inr(pays)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-zinc-900 text-white hover:bg-black disabled:opacity-40">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}
